'use strict';

var React = require('react');
var array = require('../../helpers/array.js');

const SET_LOADING = 'SET_LOADING';
const SET_DATA = 'SET_DATA';
const SET_PAGE = 'SET_PAGE';
const SET_FOCUS_ROW = 'SET_FOCUS_ROW';
const CLEAR_FOCUS_ROW = 'CLEAR_FOCUS_ROW';
const TOGGLE_ROW_SELECTED = 'TOGGLE_ROW_SELECTED';
const TOGGLE_ROW_EXPANDED = 'TOGGLE_ROW_EXPANDED';
const SET_ALL_SELECTED_ROWS = 'SET_ALL_SELECTED_ROWS';
const CLEAR_ALL_SELECTED_ROWS = 'CLEAR_ALL_SELECTED_ROWS';
const BEGIN_ROW_UPDATE = 'BEGIN_ROW_UPDATE';
const END_ROW_UPDATE = 'END_ROW_UPDATE';
const ADD_TO_QUERY_PARAMS = 'ADD_TO_QUERY_PARAMS';
const REMOVE_FROM_QUERY_PARAMS = 'REMOVE_FROM_QUERY_PARAMS';
const RESET_QUERY_PARAMS = 'RESET_QUERY_PARAMS';
const tableEngineReducer = (state, action) => {
    switch (action.type) {
        case SET_LOADING:
            return { ...state, isLoading: action.data };
        case SET_DATA:
            return { ...state, data: [...action.data], isLoading: false, total: action.total };
        case SET_PAGE:
            return { ...state, page: action.data, isLoading: state.page !== action.data };
        case SET_FOCUS_ROW:
            return { ...state, focusRow: action.data[state.keyField] };
        case CLEAR_FOCUS_ROW:
            return { ...state, focusRow: null };
        case TOGGLE_ROW_EXPANDED:
            return { ...state, expandedRows: array.addOrRemove(state.expandedRows, action.data) };
        case TOGGLE_ROW_SELECTED:
            return { ...state, selectedRows: array.addOrRemove(state.selectedRows, action.data) };
        case SET_ALL_SELECTED_ROWS:
            return { ...state, selectedRows: [...action.data] };
        case CLEAR_ALL_SELECTED_ROWS:
            return { ...state, selectedRows: [] };
        case BEGIN_ROW_UPDATE:
            return {
                ...state,
                data: state.data.map(d => (d[state.keyField] === action.data[state.keyField] ? action.data : d)),
                editingRows: array.addOrRemove(state.editingRows, action.data[state.keyField]),
            };
        case END_ROW_UPDATE:
            return {
                ...state,
                data: state.data.map(d => (d[state.keyField] === action.data[state.keyField] ? action.data : d)),
                editingRows: array.addOrRemove(state.editingRows, action.data[state.keyField]),
            };
        case ADD_TO_QUERY_PARAMS:
            return {
                ...state,
                queryParams: [...state.queryParams, action.data],
                isLoading: true,
            };
        case REMOVE_FROM_QUERY_PARAMS: {
            const nextQueryParams = array.removeItemFromArrayByKey(state.queryParams, action.data, d => d.key);
            return {
                ...state,
                queryParams: nextQueryParams,
                isLoading: state.queryParams.length !== nextQueryParams.length,
            };
        }
        case RESET_QUERY_PARAMS:
            return { ...state, queryParams: [] };
        default:
            throw new Error(`NotImplemented action - type: ${action.type}`);
    }
};
function setData(dispatch, rows, total) {
    dispatch({ type: SET_DATA, data: rows, total });
    clearFocusRow(dispatch);
    clearAllSelectedRows(dispatch);
}
function setPage(dispatch, page) {
    dispatch({ type: SET_PAGE, data: page });
}
function clearAllSelectedRows(dispatch) {
    dispatch({ type: CLEAR_ALL_SELECTED_ROWS });
}
function toggleRowExpanded(dispatch, rowKey) {
    dispatch({ type: TOGGLE_ROW_EXPANDED, data: rowKey });
}
function toggleRowSelected(dispatch, rowKey) {
    dispatch({ type: TOGGLE_ROW_SELECTED, data: rowKey });
}
function setAllSelectedRows(dispatch, rowKeys) {
    dispatch({ type: SET_ALL_SELECTED_ROWS, data: rowKeys });
}
function clearFocusRow(dispatch) {
    dispatch({ type: CLEAR_FOCUS_ROW });
}
function setFocusRow(dispatch, row) {
    dispatch({ type: SET_FOCUS_ROW, data: row });
}
function beginRowUpdate(dispatch, row) {
    dispatch({ type: BEGIN_ROW_UPDATE, data: row });
}
function endRowUpdate(dispatch, row) {
    dispatch({ type: END_ROW_UPDATE, data: row });
}
function setLoading(dispatch, isLoading) {
    dispatch({ type: SET_LOADING, data: isLoading });
}
function addToQueryParams(dispatch, queryParam, pagePreserved = false) {
    dispatch({ type: ADD_TO_QUERY_PARAMS, data: queryParam });
    if (!pagePreserved) {
        setPage(dispatch, 0);
    }
}
function removeFromQueryParams(dispatch, queryParam, pagePreserved = false) {
    dispatch({ type: REMOVE_FROM_QUERY_PARAMS, data: queryParam });
    if (!pagePreserved) {
        setPage(dispatch, 0);
    }
}
function getQueryParamByKey(queryKey, queryParams) {
    let queryParamForCol;
    for (const queryParam of queryParams) {
        if (queryParam.key === queryKey) {
            queryParamForCol = queryParam;
            break;
        }
    }
    return queryParamForCol;
}
function resetQueryParams(dispatch) {
    dispatch({ type: RESET_QUERY_PARAMS });
}
const makeTableEngine = (state, dispatch) => ({
    state,
    setLoading: (isLoading) => setLoading(dispatch, isLoading),
    setData: (rows, total) => setData(dispatch, rows, total),
    getData: () => state.data,
    getTotal: () => state.total,
    setPage: (page) => setPage(dispatch, page),
    getPage: () => state.page,
    toggleRowExpanded: (rowKey) => toggleRowExpanded(dispatch, rowKey),
    toggleRowSelected: (rowKey) => toggleRowSelected(dispatch, rowKey),
    setAllSelectedRows: (rowKeys) => setAllSelectedRows(dispatch, rowKeys),
    clearAllSelectedRows: () => clearAllSelectedRows(dispatch),
    setFocusRow: (row) => setFocusRow(dispatch, row),
    getSelectedRows: () => state.data.filter(d => state.selectedRows.includes(d[state.keyField])),
    clearFocusRow: () => clearFocusRow(dispatch),
    getFocusRow: () => state.data.find(d => state.focusRow === d[state.keyField]),
    beginRowUpdate: (row) => beginRowUpdate(dispatch, row),
    endRowUpdate: (row) => endRowUpdate(dispatch, row),
    getQueryParamByKey: (queryKey) => getQueryParamByKey(queryKey, state.queryParams),
    getQueryParams: () => state.queryParams,
    addToQueryParams: (queryParam, pagePreserved) => addToQueryParams(dispatch, queryParam, pagePreserved),
    removeFromQueryParams: (queryParam, pagePreserved) => removeFromQueryParams(dispatch, queryParam, pagePreserved),
    resetQueryParams: () => resetQueryParams(dispatch),
});
function useTable(keyField, initialData = [], initialIsLoading = true, initialPage = 0, initialQueryParams = [], initialTotal) {
    const initialState = {
        keyField,
        data: initialData,
        page: initialPage,
        total: initialTotal,
        isLoading: initialIsLoading,
        queryParams: initialQueryParams,
        focusRow: null,
        selectedRows: [],
        expandedRows: [],
        editingRows: [],
    };
    const [state, dispatch] = React.useReducer(tableEngineReducer, initialState);
    return React.useMemo(() => makeTableEngine(state, dispatch), [state]);
}

exports.useTable = useTable;
//# sourceMappingURL=useTable.js.map
